import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowsClockwise,
  Export,
  CaretDown,
  CaretUp,
  Package,
  Warning,
  CurrencyInr,
  Calculator,
  Scales,
  Percent,
  ListBullets,
  ChartBar,
  CheckCircle,
  Clock,
  PencilSimple,
} from "@phosphor-icons/react";
import { useReactToPrint } from "react-to-print";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Process names mapping with machine names from document
const PROCESS_NAMES = {
  process1: "P1 - Corrugation",
  process2: "P2 - Sheet/Paper Cutting (Legacy)",
  process2a: "P2A - Sheet Cutting Machine",
  process2b: "P2B - Paper Cutting Machine",
  process3: "P3 - Sheet Pasting Machine",
  process4: "P4 - Thin Blade Slitter Scorer",
  process5: "P5 - 4 Bar Rotary Creasing",
  process6: "P6 - RS4 Single Color Printing & Slotting",
  process6a: "P6A - Eccentric Slotter",
  process7: "P7 - Flat Bed Punching",
  process8: "P8 - Rotary Die Punching",
  process9: "P9 - Calico Pasting",
  process10: "P10 - Stitching & Bundling",
};

const ProductionTrackingDetail = () => {
  const { woNumber } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const [workOrder, setWorkOrder] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [productSpecs, setProductSpecs] = useState([]);
  const [fluteTypes, setFluteTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProcesses, setExpandedProcesses] = useState({});

  // Toggle for price calculation mode: false = average, true = detailed formula
  const [useDetailedCalculation, setUseDetailedCalculation] = useState(false);

  // Toggle for price editing mode
  const [enablePriceEditing, setEnablePriceEditing] = useState(false);

  // Editable prices per process
  const [customPrices, setCustomPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);

  // Save custom prices to backend
  const handleSavePrices = async () => {
    setSavingPrices(true);
    try {
      await axios.post(`${API}/work-orders/${woNumber}/custom-prices`, { prices: customPrices });
      toast.success("Prices saved successfully");
    } catch (err) {
      toast.error("Failed to save prices");
    } finally {
      setSavingPrices(false);
    }
  };

  // Load saved custom prices
  const fetchCustomPrices = async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${woNumber}/custom-prices`);
      if (res.data?.prices && Object.keys(res.data.prices).length > 0) {
        setCustomPrices(res.data.prices);
        setEnablePriceEditing(true);
      }
    } catch (err) { /* no saved prices yet */ }
  };

  // Checkboxes for expanded view options
  const [viewOptions, setViewOptions] = useState({
    reelBreakdown: true,
    formulaCalculation: true,
    plannedVsActual: true,
    processFlowLoss: true,
  });

  // Previous WO loss % benchmark: per-process loss from the most recent
  // closed work order with the same item_name. Surfaced in the per-process
  // row as "Prev WO Loss %" plus inline in the PFL panel for comparison.
  const [prevWoLossByProcess, setPrevWoLossByProcess] = useState(null);
  const [prevWoLossWoNumber, setPrevWoLossWoNumber] = useState(null);

  const fetchPrevWoLoss = async (woIdent) => {
    try {
      const res = await axios.get(`${API}/work-orders/${encodeURIComponent(woIdent)}/pfl-history`);
      const items = res.data?.history || [];
      if (items.length === 0) {
        setPrevWoLossByProcess(null);
        setPrevWoLossWoNumber(null);
        return;
      }
      setPrevWoLossByProcess(items[0].loss_pct_by_process || {});
      setPrevWoLossWoNumber(items[0].work_order_number);
    } catch (e) {
      setPrevWoLossByProcess(null);
      setPrevWoLossWoNumber(null);
    }
  };

  useEffect(() => {
    fetchWorkOrderData();
    fetchInventory();
    fetchProductSpecs();
    fetchCustomPrices();
    fetchFluteTypes();
  }, [woNumber]);

  const fetchWorkOrderData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/production-tracking`);
      const wo = response.data.find((d) => d.work_order_number === woNumber);
      if (wo) {
        setWorkOrder(wo);
        // Kick off the previous-work-order benchmark fetch
        fetchPrevWoLoss(wo.work_order_number || wo.id);
      } else {
        toast.error("Work order not found");
        navigate("/admin/production-tracking");
      }
    } catch (error) {
      console.error("Error fetching work order data:", error);
      toast.error("Failed to load work order data");
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchProductSpecs = async () => {
    try {
      const response = await axios.get(`${API}/product-specifications`);
      setProductSpecs(response.data);
    } catch (error) {
      console.error("Error fetching product specifications:", error);
    }
  };

  const fetchFluteTypes = async () => {
    try {
      const response = await axios.get(`${API}/flute-types`);
      setFluteTypes(response.data);
    } catch (error) {
      console.error("Error fetching flute types:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Production-Tracking-${woNumber}`,
  });

  const toggleProcessExpand = (processKey) => {
    setExpandedProcesses((prev) => ({
      ...prev,
      [processKey]: !prev[processKey],
    }));
  };

  // Update custom price for a process
  const updateCustomPrice = (processKey, priceType, value) => {
    setCustomPrices((prev) => ({
      ...prev,
      [processKey]: {
        ...prev[processKey],
        [priceType]: parseFloat(value) || 0,
      },
    }));
  };

  // Get price from inventory for a reel
  const getReelPrice = (reelId) => {
    const reel = inventory.find((i) => i.reel_id === reelId);
    return parseFloat(reel?.paper_price) || 0;
  };

  // Extract GSM from rm_name pattern (e.g., NS18018 → 180, GYT12020 → 120)
  const extractGSMFromRmName = (rmName) => {
    if (!rmName) return 0;
    const match = rmName.match(/[A-Za-z]+(\d{3})\d{2}/);
    return match ? parseInt(match[1]) : 0;
  };

  // Get GSM from inventory for a reel
  const getReelGSM = (reelId) => {
    const reel = inventory.find((i) => i.reel_id === reelId);
    if (!reel) return 180;
    return parseFloat(reel?.gsm) || parseFloat(reel?.page_no) || extractGSMFromRmName(reel?.rm_name) || 180;
  };

  // Get flute multiplier from WO flute_type name
  const getFluteMultiplier = (fluteTypeName) => {
    if (!fluteTypeName) return 150;
    const ft = fluteTypes.find(f => f.name === fluteTypeName);
    return ft ? ft.multiplier : 150;
  };

  // Get item rate/price from Product & Material Info
  const getItemRate = () => {
    // First try to get from work order header (new format)
    if (workOrder?.header?.item_rate) {
      return parseFloat(workOrder.header.item_rate) || 0;
    }
    
    // Fallback: Try to match by item_name or product_id from product specifications
    const itemName = workOrder?.header?.item_name || workOrder?.item_name;
    const productId = workOrder?.header?.product_id;
    
    if (productId && productSpecs.length > 0) {
      const spec = productSpecs.find(s => s.product_id === productId);
      if (spec?.rate) return parseFloat(spec.rate) || 0;
    }
    
    if (itemName && productSpecs.length > 0) {
      const spec = productSpecs.find(s => s.item_name === itemName);
      if (spec?.rate) return parseFloat(spec.rate) || 0;
    }
    
    // Default fallback
    return 5; // ₹5 per board as fallback
  };

  // Check if a process is completed (has actual data)
  const isProcessCompleted = (processKey) => {
    if (!workOrder) return false;
    const actuals = workOrder.process_actuals || {};
    const pActuals = actuals[processKey] || {};
    
    // Check various actual fields that indicate completion. Manual PFL entry
    // (pfl_actual) is the authoritative source going forward.
    return !!(
      pActuals.pfl_actual !== undefined && pActuals.pfl_actual !== null && pActuals.pfl_actual !== "" ||
      pActuals.actual_sheet_counting ||
      pActuals.actual_board_counting ||
      pActuals.actual_paper_counting ||
      pActuals.actual_total_boxes ||
      pActuals.actual_used_base_weight ||
      pActuals.actual_used_flute_weight
    );
  };

  // Calculate loss for a specific process based on document formulas
  const calculateProcessLoss = (processKey) => {
    if (!workOrder) return null;

    const processes = workOrder.processes || {};
    const actuals = workOrder.process_actuals || {};
    const p1 = processes.process1 || {};
    const p2Legacy = processes.process2 || {};

    // Get header data
    const reelSize = parseFloat(workOrder.header?.reel_size) || parseFloat(p2Legacy.reel_size) || 0;
    const cuttingSize = parseFloat(workOrder.header?.cutting_size) || parseFloat(p2Legacy.cutting_size) || 0;

    // Get total assigned weight for the entire work order (for percentage calculation)
    let totalWOAssignedWeight = 0;
    if (p1.rows && Array.isArray(p1.rows)) {
      p1.rows.forEach((row) => {
        totalWOAssignedWeight += parseFloat(row.base_weight) || 0;
        totalWOAssignedWeight += parseFloat(row.flute_weight) || 0;
      });
    }

    // Process-specific calculations based on document
    if (processKey === "process1") {
      // P1 - Corrugation: Loss is calculated based on P2A sheet counting
      // After P1 completes, raw reels convert to corrugated sheets/bits
      // The loss from corrugation is identified when comparing expected sheets vs actual sheets in P2A
      
      let totalBaseWeight = 0;
      let totalFluteWeight = 0;
      let basePrice = 0;
      let flutePrice = 0;
      let baseGSM = 180;
      let fluteGSM = 180;
      let baseStockId = "";
      let fluteStockId = "";

      if (p1.rows && Array.isArray(p1.rows)) {
        p1.rows.forEach((row) => {
          const baseWeight = parseFloat(row.base_weight) || 0;
          const fluteWeight = parseFloat(row.flute_weight) || 0;
          totalBaseWeight += baseWeight;
          totalFluteWeight += fluteWeight;

          if (row.base_reel_id) {
            basePrice = getReelPrice(row.base_reel_id);
            baseGSM = getReelGSM(row.base_reel_id);
            baseStockId = row.base_reel_id;
          }
          if (row.flute_reel_id) {
            flutePrice = getReelPrice(row.flute_reel_id);
            fluteGSM = getReelGSM(row.flute_reel_id);
            fluteStockId = row.flute_reel_id;
          }
        });
      }

      const p1Actuals = actuals.process1 || {};
      const usedBaseWeight = parseFloat(p1Actuals.actual_used_base_weight) || 0;
      const usedFluteWeight = parseFloat(p1Actuals.actual_used_flute_weight) || 0;
      const totalUsedWeight = usedBaseWeight + usedFluteWeight;

      // P1 loss is calculated via P2A - we need P2A actual sheet count
      const p2aActuals = actuals.process2a || actuals.process2 || {};
      const p2aPlanned = processes.process2a || processes.process2 || {};
      const actualSheetsFromP2A = parseInt(p2aActuals.actual_sheet_counting) || 0;
      const plannedSheetsFromP2A = parseInt(p2aPlanned.sheet_counting) || 0;

      // Calculate expected sheets from corrugation output weight
      // Total GSM = Base GSM + (Flute GSM × Flute Multiplier%)
      const fluteMultiplier = getFluteMultiplier(p1.flute_type);
      const totalGSM = baseGSM + (fluteGSM * (fluteMultiplier / 100));
      // Single sheet weight (kg) = (Reel Size × Cutting Size × Total GSM) / 1550 / 1000
      const singleSheetWeightKg = (reelSize * cuttingSize * totalGSM) / 1550 / 1000;
      
      // Expected sheets from total used weight in corrugation
      const expectedSheetsFromWeight = totalUsedWeight > 0 && singleSheetWeightKg > 0 
        ? Math.floor(totalUsedWeight / singleSheetWeightKg)
        : 0;

      // Use the higher of planned sheets or calculated sheets as the expected value
      const expectedSheets = Math.max(plannedSheetsFromP2A, expectedSheetsFromWeight);

      // Sheet loss = Expected - Actual (only if P2A has actual data)
      const sheetLoss = actualSheetsFromP2A > 0 ? Math.abs(expectedSheets - actualSheetsFromP2A) : 0;
      
      // Weight loss = Sheet loss × Single sheet weight
      const weightLoss = sheetLoss * singleSheetWeightKg;

      // Use custom prices if editing is enabled
      const effectiveBasePrice = enablePriceEditing && customPrices.process1?.basePrice !== undefined
        ? customPrices.process1.basePrice
        : basePrice;
      const effectiveFlutePrice = enablePriceEditing && customPrices.process1?.flutePrice !== undefined
        ? customPrices.process1.flutePrice
        : flutePrice;
      const avgPrice = (effectiveBasePrice + effectiveFlutePrice) / 2;

      // Value loss = Weight loss × Average price
      const valueLoss = weightLoss * avgPrice;
      const percentageLoss = totalWOAssignedWeight > 0 ? (weightLoss / totalWOAssignedWeight) * 100 : 0;

      // Check if P2A is completed to determine if we can calculate loss
      const canCalculateLoss = actualSheetsFromP2A > 0;

      return {
        processName: PROCESS_NAMES[processKey],
        hasData: totalBaseWeight > 0 || totalFluteWeight > 0,
        hasActualData: usedBaseWeight > 0 || usedFluteWeight > 0,
        isCompleted: isProcessCompleted(processKey),
        weightLoss: canCalculateLoss ? weightLoss.toFixed(2) : "Awaiting P2A",
        pricePerKg: avgPrice > 0 ? avgPrice.toFixed(2) : "—",
        valueLoss: canCalculateLoss ? valueLoss.toFixed(2) : "—",
        percentageLoss: canCalculateLoss ? percentageLoss.toFixed(2) : "—",
        details: {
          reelSize,
          cuttingSize,
          baseGSM,
          fluteGSM,
          totalGSM: totalGSM.toFixed(2),
          fluteMultiplier,
          singleSheetWeightKg: singleSheetWeightKg.toFixed(6),
          baseStockId,
          fluteStockId,
          // Planned (from WO creation)
          plannedBaseWeight: totalBaseWeight,
          plannedFluteWeight: totalFluteWeight,
          // Used (from Material Usage / Corrugation output)
          usedBaseWeight,
          usedFluteWeight,
          totalUsedWeight,
          // Sheet calculation
          expectedSheetsFromWeight,
          plannedSheetsFromP2A,
          expectedSheets,
          actualSheetsFromP2A,
          sheetLoss,
          // Loss calculation
          weightLoss: weightLoss.toFixed(2),
          canCalculateLoss,
          // Prices
          basePrice: effectiveBasePrice,
          flutePrice: effectiveFlutePrice,
          avgPrice,
          // Value loss
          valueLoss: valueLoss.toFixed(2),
          rows: p1.rows || [],
        },
      };
    }

    if (processKey === "process2a" || processKey === "process2") {
      // P2A - Sheet Cutting: Calculate using document formula
      const p2a = processes.process2a || {};
      const p2aActuals = actuals.process2a || actuals.process2 || {};
      const useP1Output = p2a.use_p1_output === true;

      // When P2A uses P1 output, show combined name
      const combinedProcessName = useP1Output 
        ? "P1 + P2A - Corrugation & Sheet Cutting"
        : (PROCESS_NAMES[processKey] || PROCESS_NAMES.process2a);

      // Get GSM values from P1 reels
      let baseGSM = 180;
      let fluteGSM = 180;
      let avgPrice = 0;

      if (p1.rows && p1.rows.length > 0) {
        const row = p1.rows[0];
        baseGSM = getReelGSM(row.base_reel_id);
        fluteGSM = getReelGSM(row.flute_reel_id);
        avgPrice = (getReelPrice(row.base_reel_id) + getReelPrice(row.flute_reel_id)) / 2;
      }

      // Total GSM: Base GSM + (Flute GSM × Flute Multiplier%)
      const fluteMultiplier = getFluteMultiplier(p1.flute_type);
      const totalGSM = baseGSM + (fluteGSM * (fluteMultiplier / 100));

      // Single sheet weight (grams) from document: (Reel Size × Cutting Size × Total GSM) / 1550
      const singleSheetWeightGrams = (reelSize * cuttingSize * totalGSM) / 1550;
      const singleSheetWeightKg = singleSheetWeightGrams / 1000;

      // Get used total stock reel weight from P1 (corrugation process)
      const p1Actuals = actuals.process1 || {};
      const usedTotalReelWeight = (parseFloat(p1Actuals.actual_used_base_weight) || 0) + 
                                   (parseFloat(p1Actuals.actual_used_flute_weight) || 0);

      // Planned sheet counting from document: Used total stock reel weight / Single sheet weight
      const plannedSheetsFromWeight = usedTotalReelWeight > 0 && singleSheetWeightKg > 0
        ? Math.floor(usedTotalReelWeight / singleSheetWeightKg)
        : parseInt(p2a.sheet_counting) || 0;

      // Use planned from WO if available, otherwise calculate
      const plannedSheets = parseInt(p2a.sheet_counting) || plannedSheetsFromWeight;
      const actualSheets = parseInt(p2aActuals.actual_sheet_counting) || 0;

      // Material loss quantity (absolute value - both over and under is loss)
      const sheetLoss = Math.abs(plannedSheets - actualSheets);

      // Material loss weight from document: Loss quantity × Single sheet weight
      const materialLossWeight = sheetLoss * singleSheetWeightKg;

      // Use custom price if editing enabled
      const effectivePrice = enablePriceEditing && customPrices.process2a?.avgPrice !== undefined
        ? customPrices.process2a.avgPrice
        : avgPrice;

      // Value of loss from document: Material Loss Weight × Average Price
      const valueLoss = materialLossWeight * effectivePrice;
      const percentageLoss = totalWOAssignedWeight > 0 ? (materialLossWeight / totalWOAssignedWeight) * 100 : 0;

      // Detailed formula for separate base/flute calculation
      let baseWeightLoss = 0;
      let fluteWeightLoss = 0;
      let baseValueLoss = 0;
      let fluteValueLoss = 0;

      if (useDetailedCalculation) {
        const baseWeightPerSheet = (reelSize * cuttingSize * baseGSM) / 1550 / 1000;
        const fluteWeightPerSheet = (reelSize * cuttingSize * (fluteGSM * (fluteMultiplier / 100))) / 1550 / 1000;

        baseWeightLoss = sheetLoss * baseWeightPerSheet;
        fluteWeightLoss = sheetLoss * fluteWeightPerSheet;

        if (p1.rows && p1.rows.length > 0) {
          const basePrice = enablePriceEditing && customPrices.process2a?.basePrice !== undefined
            ? customPrices.process2a.basePrice
            : getReelPrice(p1.rows[0].base_reel_id);
          const flutePrice = enablePriceEditing && customPrices.process2a?.flutePrice !== undefined
            ? customPrices.process2a.flutePrice
            : getReelPrice(p1.rows[0].flute_reel_id);
          baseValueLoss = baseWeightLoss * basePrice;
          fluteValueLoss = fluteWeightLoss * flutePrice;
        }
      }

      const finalValueLoss = useDetailedCalculation ? (baseValueLoss + fluteValueLoss) : valueLoss;

      return {
        processName: combinedProcessName,
        hasData: plannedSheets > 0 || actualSheets > 0,
        hasActualData: actualSheets > 0,
        isCompleted: isProcessCompleted(processKey),
        weightLoss: materialLossWeight.toFixed(2),
        pricePerKg: effectivePrice.toFixed(2),
        valueLoss: finalValueLoss.toFixed(2),
        percentageLoss: percentageLoss.toFixed(2),
        details: {
          reelSize,
          cuttingSize,
          baseGSM,
          fluteGSM,
          totalGSM: totalGSM.toFixed(2),
          fluteMultiplier,
          singleSheetWeightGrams: singleSheetWeightGrams.toFixed(3),
          singleSheetWeightKg: singleSheetWeightKg.toFixed(6),
          usedTotalReelWeight: usedTotalReelWeight.toFixed(2),
          plannedSheetsFromWeight,
          plannedSheets,
          actualSheets,
          sheetLoss,
          materialLossWeight: materialLossWeight.toFixed(2),
          defaultPrice: avgPrice,
          effectivePrice,
          baseWeightLoss: baseWeightLoss.toFixed(2),
          fluteWeightLoss: fluteWeightLoss.toFixed(2),
          baseValueLoss: baseValueLoss.toFixed(2),
          fluteValueLoss: fluteValueLoss.toFixed(2),
          useDetailed: useDetailedCalculation,
        },
      };
    }

    if (processKey === "process2b") {
      // P2B - Paper Cutting from document
      const p2b = processes.process2b || {};
      const p2bActuals = actuals.process2b || {};

      // Get GSM for paper (typically different from corrugated GSM)
      let paperGSM = 180;
      if (p2b.reels && p2b.reels.length > 0 && p2b.reels[0].reel_no) {
        paperGSM = getReelGSM(p2b.reels[0].reel_no);
      }

      // Single paper weight from document: (Reel Size × Cutting Size × GSM) / 1550
      const singlePaperWeightGrams = (reelSize * cuttingSize * paperGSM) / 1550;
      const singlePaperWeightKg = singlePaperWeightGrams / 1000;

      const plannedPapers = parseInt(p2b.paper_counting) || 0;
      const actualPapers = parseInt(p2bActuals.actual_paper_counting) || 0;

      // Material loss (absolute value)
      const paperLoss = Math.abs(plannedPapers - actualPapers);

      // Material loss weight
      const materialLossWeight = paperLoss * singlePaperWeightKg;

      // Get price from inventory
      let avgPrice = 0;
      if (p2b.reels && p2b.reels.length > 0 && p2b.reels[0].reel_no) {
        avgPrice = getReelPrice(p2b.reels[0].reel_no);
      }

      const effectivePrice = enablePriceEditing && customPrices.process2b?.avgPrice !== undefined
        ? customPrices.process2b.avgPrice
        : avgPrice;

      const valueLoss = materialLossWeight * effectivePrice;
      const percentageLoss = totalWOAssignedWeight > 0 ? (materialLossWeight / totalWOAssignedWeight) * 100 : 0;

      return {
        processName: PROCESS_NAMES[processKey],
        hasData: plannedPapers > 0 || actualPapers > 0,
        hasActualData: actualPapers > 0,
        isCompleted: isProcessCompleted(processKey),
        weightLoss: materialLossWeight.toFixed(2),
        pricePerKg: effectivePrice.toFixed(2),
        valueLoss: valueLoss.toFixed(2),
        percentageLoss: percentageLoss.toFixed(2),
        details: {
          reelSize,
          cuttingSize,
          paperGSM,
          singlePaperWeightGrams: singlePaperWeightGrams.toFixed(3),
          singlePaperWeightKg: singlePaperWeightKg.toFixed(6),
          plannedPapers,
          actualPapers,
          paperLoss,
          materialLossWeight: materialLossWeight.toFixed(2),
          defaultPrice: avgPrice,
          effectivePrice,
        },
      };
    }

    // Process 3-10: Board counting processes
    // From document: Board lost = Planned Board count - Actual board count
    // Value of loss = Board lost × Price (from Prod & Material Info)
    if (["process3", "process4", "process5", "process6", "process6a", "process7", "process8", "process10"].includes(processKey)) {
      const pData = processes[processKey] || {};
      const pActuals = actuals[processKey] || {};

      // Get planned board count
      let plannedBoards = 0;
      if (processKey === "process3") {
        plannedBoards = parseInt(pData.sheet_counting) || parseInt(pActuals.planned_sheet_counting) || 0;
      } else if (processKey === "process10") {
        // For P10, planned board count = total number of boxes
        plannedBoards = parseInt(pData.total_boxes) || parseInt(pActuals.planned_total_boxes) || 0;
      } else if (processKey === "process8") {
        // P8 (Rotary Die Punching) stores planned in die_countings or counting field
        if (pData.die_countings && typeof pData.die_countings === 'object') {
          plannedBoards = Object.values(pData.die_countings).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
        }
        if (!plannedBoards) {
          plannedBoards = parseInt(pData.counting) || parseInt(pData.board_counting) || parseInt(pActuals.planned_board_counting) || 0;
        }
      } else {
        plannedBoards = parseInt(pData.board_counting) || parseInt(pActuals.planned_board_counting) || 0;
      }

      const actualBoards = parseInt(pActuals.actual_board_counting) || parseInt(pActuals.actual_total_boxes) || 0;

      // Board loss (absolute value)
      const boardLoss = Math.abs(plannedBoards - actualBoards);

      // Get price from Prod & Material Info (item_rate from product specification)
      const boardPrice = getItemRate();

      const effectivePrice = enablePriceEditing && customPrices[processKey]?.boardPrice !== undefined
        ? customPrices[processKey].boardPrice
        : boardPrice;

      const valueLoss = boardLoss * effectivePrice;

      return {
        processName: PROCESS_NAMES[processKey],
        hasData: plannedBoards > 0 || actualBoards > 0,
        hasActualData: actualBoards > 0,
        isCompleted: isProcessCompleted(processKey),
        weightLoss: "—",
        pricePerKg: `₹${effectivePrice.toFixed(2)}/pc`,
        valueLoss: valueLoss.toFixed(2),
        percentageLoss: plannedBoards > 0 ? ((boardLoss / plannedBoards) * 100).toFixed(2) : "0.00",
        details: {
          plannedBoards,
          actualBoards,
          boardLoss,
          defaultPrice: boardPrice,
          effectivePrice,
          processType: "board",
        },
      };
    }

    // Process 9 - Calico Pasting (if needed)
    if (processKey === "process9") {
      const pActuals = actuals[processKey] || {};
      return {
        processName: PROCESS_NAMES[processKey],
        hasData: false,
        hasActualData: false,
        isCompleted: isProcessCompleted(processKey),
        weightLoss: "—",
        pricePerKg: "—",
        valueLoss: "—",
        percentageLoss: "—",
        details: {},
      };
    }

    // Default for other processes
    return {
      processName: PROCESS_NAMES[processKey] || processKey,
      hasData: false,
      hasActualData: false,
      isCompleted: false,
      weightLoss: "—",
      pricePerKg: "—",
      valueLoss: "—",
      percentageLoss: "—",
      details: {},
    };
  };

  // Process-to-Process (Chain) Loss Calculation
  // Tracks actual output flowing from one process to the next
  const calculateChainLoss = () => {
    if (!workOrder) return {};

    const processes = workOrder.processes || {};
    const actuals = workOrder.process_actuals || {};
    const selectedProcesses = workOrder.selected_processes || {};
    const upsStr = workOrder.header?.ups || '';
    const upsVal = parseInt(String(upsStr).replace(/\D/g, '')) || 1;

    // Define the full process chain — order matters. Earlier processes (P1,
    // P2A, P2B) are weight-based (Kg) while P3 onwards is Nos-based, so the
    // cross-unit "chain loss from previous" isn't meaningful between them.
    // We still include them so admins can manually enter Actual output for
    // every stage and see "Loss vs Planned" per process.
    const chainOrder = ['process1', 'process2a', 'process2b', 'process3', 'process4', 'process5', 'process6', 'process6a', 'process7', 'process8', 'process10'];

    // The first Nos-based process (P3) is treated as a fresh source so the
    // chain math doesn't try to subtract Kg from Nos.
    const nosBasedStart = 'process3';

    // Filter to only processes that actually exist in this work order
    // Check both planned process data and actuals
    const activeChain = chainOrder.filter(key => {
      const hasProcessData = processes[key] && Object.keys(processes[key]).length > 0;
      const hasActualData = actuals[key] && Object.keys(actuals[key]).length > 0;
      // If selected_processes exists, use it; otherwise fall back to checking data presence
      if (selectedProcesses && Object.keys(selectedProcesses).length > 0) {
        return selectedProcesses[key] && (hasProcessData || hasActualData);
      }
      return hasProcessData || hasActualData;
    });

    // Helper to get actual output of a process.
    // PFL Actual Output is now MANUALLY entered on the Process Flow Loss
    // panel and stored at `process_actuals.{key}.pfl_actual`. We do NOT fall
    // back to the auto-derived sheet/board counters anymore — operators must
    // type the value they observed for chain-loss accuracy.
    const getActualOutput = (processKey) => {
      const pActuals = actuals[processKey] || {};
      const v = pActuals.pfl_actual;
      if (v === null || v === undefined || v === "") return 0;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    // Helper to get planned value of a process. Handles both weight-based
    // (Kg) and counting-based (Nos) fields so P1/P2A/P2B get a planned too.
    const getPlannedValue = (processKey) => {
      const pActuals = actuals[processKey] || {};
      const pData = processes[processKey] || {};
      return parseInt(pActuals.planned_board_counting) ||
             parseInt(pActuals.planned_sheet_counting) ||
             parseInt(pActuals.planned_paper_count) ||
             parseInt(pData.board_counting) ||
             parseInt(pData.sheet_counting) ||
             parseInt(pData.paper_count) ||
             parseInt(pData.total_papers) ||
             parseInt(pData.total_sheets) ||
             parseInt(pData.counting) ||
             parseInt(pData.total_boxes) || 0;
    };

    // If UPS not in header, try to calculate from P3→P4 planned ratio
    let effectiveUps = upsVal;
    if (effectiveUps <= 1 && activeChain.includes('process3')) {
      const p3Idx = activeChain.indexOf('process3');
      const p3Planned = getPlannedValue('process3');
      const nextKey = activeChain[p3Idx + 1]; // first process after P3 (if any)
      const nextPlanned = nextKey ? getPlannedValue(nextKey) : 0;
      if (p3Planned > 0 && nextPlanned > 0) {
        const ratio = Math.round(nextPlanned / p3Planned);
        if (ratio > 1) effectiveUps = ratio;
      }
    }

    const chainResults = {};
    let previousActualOutput = 0;

    activeChain.forEach((processKey, idx) => {
      const actualOutput = getActualOutput(processKey);
      const plannedQty = getPlannedValue(processKey);

      // Treat the first process AND the first Nos-based process as a Source.
      // This avoids meaningless cross-unit chain math (Kg → Nos at P3).
      const isP3FirstNos = processKey === nosBasedStart && previousActualOutput > 0;
      const isSource = (idx === 0) || isP3FirstNos;

      if (isSource) {
        chainResults[processKey] = {
          expectedInput: null,
          actualOutput,
          planned: plannedQty,
          chainLoss: null,
          chainLossPercent: null,
          plannedLoss: plannedQty > 0 ? plannedQty - actualOutput : 0,
          plannedLossPercent: plannedQty > 0 ? (((plannedQty - actualOutput) / plannedQty) * 100).toFixed(2) : null,
          isSource: true,
          sourceLabel: `${PROCESS_NAMES[processKey]} Output`,
          upsApplied: false,
          effectiveUps,
        };
        previousActualOutput = actualOutput;
      } else {
        // Determine expected input from previous process
        let expectedInput = previousActualOutput;
        let upsApplied = false;

        // Apply UPS multiplication when transitioning FROM P3 to any subsequent process
        const prevKey = activeChain[idx - 1];
        if (prevKey === 'process3' && effectiveUps > 1) {
          expectedInput = previousActualOutput * effectiveUps;
          upsApplied = true;
        }

        const chainLoss = expectedInput > 0 ? expectedInput - actualOutput : 0;
        const chainLossPercent = expectedInput > 0 ? ((chainLoss / expectedInput) * 100) : 0;

        chainResults[processKey] = {
          expectedInput,
          actualOutput,
          planned: plannedQty,
          chainLoss,
          chainLossPercent: chainLossPercent.toFixed(2),
          plannedLoss: plannedQty > 0 ? plannedQty - actualOutput : 0,
          plannedLossPercent: plannedQty > 0 ? (((plannedQty - actualOutput) / plannedQty) * 100).toFixed(2) : null,
          isSource: false,
          previousProcess: PROCESS_NAMES[prevKey] || prevKey,
          upsApplied,
          upsVal: effectiveUps,
          previousActual: previousActualOutput,
        };

        // For the next process, use THIS process's actual output as the base
        // If actual is 0 (not recorded yet), carry forward the expected input
        previousActualOutput = actualOutput > 0 ? actualOutput : expectedInput;
      }
    });

    return chainResults;
  };

  // Memoize chain loss results
  const chainLossData = React.useMemo(() => calculateChainLoss(), [workOrder, enablePriceEditing, customPrices]);

  // ---- Process Flow Loss: manual Actual Output entry ---------------------
  const [pflEditing, setPflEditing] = React.useState({});  // { processKey: localValue }
  const [pflSaving, setPflSaving] = React.useState({});

  const getPflActualValue = (processKey) => {
    const v = workOrder?.process_actuals?.[processKey]?.pfl_actual;
    if (v === null || v === undefined || v === "") return "";
    return String(v);
  };

  const onPflActualChange = (processKey, val) => {
    setPflEditing((prev) => ({ ...prev, [processKey]: val }));
  };

  const savePflActual = async (processKey) => {
    // The /reports/production-tracking endpoint returns work orders identified
    // by work_order_number (no top-level id). The backend PATCH handler accepts
    // either, so we send whichever we have.
    const woIdent = workOrder?.id || workOrder?.work_order_number || workOrder?.header?.work_order_number;
    if (!woIdent) return;
    const localVal = pflEditing[processKey];
    const original = getPflActualValue(processKey);
    const valueToSave = localVal === undefined ? original : localVal;
    if (String(valueToSave) === String(original)) return;  // nothing to save
    setPflSaving((prev) => ({ ...prev, [processKey]: true }));
    try {
      await axios.patch(`${BACKEND_URL}/api/work-orders/${encodeURIComponent(woIdent)}/pfl-actual`, {
        process: processKey,
        value: valueToSave === "" ? null : valueToSave,
      });
      // Refresh in-memory state
      setWorkOrder((wo) => {
        if (!wo) return wo;
        const next = { ...wo, process_actuals: { ...(wo.process_actuals || {}) } };
        next.process_actuals[processKey] = { ...(next.process_actuals[processKey] || {}) };
        if (valueToSave === "" || valueToSave === null) {
          delete next.process_actuals[processKey].pfl_actual;
          // Removing the value also un-completes the process to mirror the
          // backend behavior.
          next.completed_processes = (next.completed_processes || []).filter((p) => p !== processKey);
        } else {
          next.process_actuals[processKey].pfl_actual = parseFloat(valueToSave);
          // Auto-complete: backend also added this to completed_processes.
          if (!Array.isArray(next.completed_processes)) next.completed_processes = [];
          if (!next.completed_processes.includes(processKey)) {
            next.completed_processes = [...next.completed_processes, processKey];
          }
        }
        return next;
      });
      // Clear the local edit buffer so getPflActualValue takes over
      setPflEditing((prev) => {
        const cp = { ...prev }; delete cp[processKey]; return cp;
      });
      toast.success("Actual Output saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save Actual Output");
    } finally {
      setPflSaving((prev) => { const cp = { ...prev }; delete cp[processKey]; return cp; });
    }
  };

  // Get all processes that have data
  const getProcessesWithData = () => {
    if (!workOrder) return [];

    const processes = workOrder.processes || {};
    const actuals = workOrder.process_actuals || {};
    const selectedProcesses = workOrder.selected_processes || {};

    const processKeys = [
      "process1",
      "process2a",
      "process2b",
      "process3",
      "process4",
      "process5",
      "process6",
      "process6a",
      "process7",
      "process8",
      // process9 (Calico Pasting) intentionally omitted from Production
      // Tracking — per ops feedback Feb 2026 it provides no useful loss data.
      "process10",
    ];

    // Include legacy process2 if process2a doesn't exist AND process2 has data
    if (!processes.process2a && !actuals.process2a && (processes.process2 || actuals.process2)) {
      processKeys.splice(1, 0, "process2");
    }

    // When P2A uses P1 output, combine P1+P2A: skip P1 row since loss is only measurable after sheet cutting
    const useP1Output = (processes.process2a || {}).use_p1_output === true;

    return processKeys
      .filter((key) => !(useP1Output && key === "process1"))
      .map((key) => {
        const base = { key, ...calculateProcessLoss(key) };
        // Override with manual PFL actual entry when present — this is the
        // single source of truth requested by the operator (manual entry
        // replaces automated planned-vs-actual derivation).
        const pflActual = actuals?.[key]?.pfl_actual;
        if (pflActual !== undefined && pflActual !== null && pflActual !== "") {
          // Derive planned from the process/actuals shapes we already use.
          const pInfo = processes[key] || {};
          const pAct = actuals[key] || {};
          const planned = parseInt(pAct.planned_board_counting)
            || parseInt(pAct.planned_sheet_counting)
            || parseInt(pAct.planned_paper_count)
            || parseInt(pInfo.board_counting)
            || parseInt(pInfo.sheet_counting)
            || parseInt(pInfo.paper_count)
            || parseInt(pInfo.total_papers)
            || parseInt(pInfo.total_sheets)
            || parseInt(pInfo.counting)
            || parseInt(pInfo.total_boxes)
            || 0;
          const actualNum = parseFloat(pflActual) || 0;
          const lossPct = planned > 0 ? (((planned - actualNum) / planned) * 100).toFixed(2) : "0.00";
          const lossQty = planned > 0 ? (planned - actualNum).toFixed(0) : "—";
          return {
            ...base,
            hasActualData: true,
            percentageLoss: lossPct,
            weightLoss: lossQty === "—" ? base.weightLoss : lossQty,
            valueLoss: base.valueLoss,
          };
        }
        return base;
      })
      // Show process if: has planned data, has actual data, or was selected in WO creation
      .filter((p) => p.hasData || p.hasActualData || selectedProcesses[p.key]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <ArrowsClockwise className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Work order not found</p>
      </div>
    );
  }

  const processesWithData = getProcessesWithData();

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/production-tracking")}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="border-l border-slate-200 pl-4">
              <h1 className="font-heading text-xl font-medium tracking-tight text-slate-900">
                {woNumber}
              </h1>
              <p className="text-sm text-slate-700">{workOrder.customer_name} • {workOrder.item_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchWorkOrderData}
              className="rounded-sm border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="refresh-detail-btn"
            >
              <ArrowsClockwise className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="rounded-sm bg-slate-900 text-white hover:bg-slate-800"
              data-testid="export-detail-btn"
            >
              <Export className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-6" ref={printRef}>
        {/* Controls Panel */}
        <div className="bg-white border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Price Calculation Toggles */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={useDetailedCalculation}
                  onCheckedChange={setUseDetailedCalculation}
                  id="calculation-mode"
                  data-testid="calculation-toggle"
                />
                <Label htmlFor="calculation-mode" className="text-sm text-slate-800">
                  {useDetailedCalculation ? "Detailed Formula (Separate Base/Flute)" : "Average Price"}
                </Label>
              </div>
              <div className="border-l border-slate-200 pl-6 flex items-center gap-2">
                <Switch
                  checked={enablePriceEditing}
                  onCheckedChange={setEnablePriceEditing}
                  id="price-edit-mode"
                  data-testid="price-edit-toggle"
                />
                <Label htmlFor="price-edit-mode" className="text-sm text-slate-800 flex items-center gap-1">
                  <PencilSimple className="h-4 w-4" />
                  Enable Price Editing
                </Label>
                {enablePriceEditing && Object.keys(customPrices).length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleSavePrices}
                    disabled={savingPrices}
                    className="ml-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3"
                    data-testid="save-prices-btn"
                  >
                    {savingPrices ? "Saving..." : "Save Prices"}
                  </Button>
                )}
              </div>
            </div>

            {/* View Options Checkboxes */}
            <div className="flex items-center gap-6">
              <span className="text-xs text-slate-700 uppercase tracking-wider font-medium">Show in dropdown:</span>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={viewOptions.reelBreakdown}
                  onCheckedChange={(checked) => setViewOptions((prev) => ({ ...prev, reelBreakdown: checked }))}
                  id="view-reel"
                  data-testid="view-reel-checkbox"
                />
                <Label htmlFor="view-reel" className="text-sm text-slate-800">
                  <ListBullets className="h-4 w-4 inline mr-1" />
                  Reel Breakdown
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={viewOptions.formulaCalculation}
                  onCheckedChange={(checked) => setViewOptions((prev) => ({ ...prev, formulaCalculation: checked }))}
                  id="view-formula"
                  data-testid="view-formula-checkbox"
                />
                <Label htmlFor="view-formula" className="text-sm text-slate-800">
                  <Calculator className="h-4 w-4 inline mr-1" />
                  Formula
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={viewOptions.plannedVsActual}
                  onCheckedChange={(checked) => setViewOptions((prev) => ({ ...prev, plannedVsActual: checked }))}
                  id="view-planned"
                  data-testid="view-planned-checkbox"
                />
                <Label htmlFor="view-planned" className="text-sm text-slate-800">
                  <ChartBar className="h-4 w-4 inline mr-1" />
                  Planned vs Actual
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={viewOptions.processFlowLoss}
                  onCheckedChange={(checked) => setViewOptions((prev) => ({ ...prev, processFlowLoss: checked }))}
                  id="view-flow"
                  data-testid="view-flow-checkbox"
                />
                <Label htmlFor="view-flow" className="text-sm text-slate-800">
                  <ListBullets className="h-4 w-4 inline mr-1" />
                  Process Flow Loss
                </Label>
              </div>
            </div>
          </div>
        </div>

        {/* Process Table */}
        <div className="bg-white border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  Process
                </th>
                <th className="px-4 py-3 text-center text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  <Scales className="h-3 w-3 inline mr-1" />
                  Weight/Qty Loss
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  <CurrencyInr className="h-3 w-3 inline mr-1" />
                  Price
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  <Percent className="h-3 w-3 inline mr-1" />
                  Loss %
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] font-semibold text-slate-700">
                  Prev WO Loss %
                </th>
              </tr>
            </thead>
            <tbody>
              {processesWithData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" weight="light" />
                    <p className="font-medium text-slate-500">No process data available</p>
                    <p className="text-sm text-slate-400 mt-1">Process data will appear after work order is started</p>
                  </td>
                </tr>
              ) : (
                processesWithData.map((process) => {
                  const isExpanded = expandedProcesses[process.key];
                  const hasLoss = parseFloat(process.valueLoss) > 0;

                  return (
                    <React.Fragment key={process.key}>
                      {/* Summary Row */}
                      <tr
                        className={`border-b border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors ${
                          !process.hasActualData ? "opacity-60" : ""
                        }`}
                        onClick={() => toggleProcessExpand(process.key)}
                        data-testid={`process-row-${process.key}`}
                      >
                        <td className="px-4 py-3">
                          <button className="p-1 hover:bg-slate-100 rounded-sm transition-colors">
                            {isExpanded ? (
                              <CaretUp className="h-4 w-4 text-slate-500" />
                            ) : (
                              <CaretDown className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">{process.processName}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {process.isCompleted ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs uppercase font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                              <CheckCircle className="h-3 w-3" weight="fill" />
                              Completed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs uppercase font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded">
                              <Clock className="h-3 w-3" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          <span className={hasLoss ? "text-red-600 font-medium" : "text-slate-800"}>
                            {process.weightLoss !== "—" ? `${process.weightLoss} Kg` : process.details?.boardLoss !== undefined ? `${process.details.boardLoss} pcs` : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-slate-800">
                          {process.pricePerKg !== "—" ? (process.pricePerKg.includes("₹") ? process.pricePerKg : `₹${process.pricePerKg}/Kg`) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          <span className={hasLoss ? "text-red-600 font-medium" : "text-slate-800"}>
                            {process.percentageLoss !== "—" ? `${process.percentageLoss}%` : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {prevWoLossByProcess?.[process.key] !== undefined ? (
                            <span className="text-slate-600">
                              {prevWoLossByProcess[process.key]}%
                              {prevWoLossWoNumber && (
                                <span className="block text-[10px] text-slate-400 normal-case tracking-normal font-sans">
                                  ({prevWoLossWoNumber})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className="p-0 bg-slate-50 border-b border-slate-200">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                                  {/* Reel/Material Breakdown for P1 */}
                                  {viewOptions.reelBreakdown && (process.key === "process1" || process.key === "process2a") && (
                                    (() => {
                                      // Reel breakdown rows live on process1.rows. When the WO uses the
                                      // combined "P1 + P2A" view (useP1Output), the P1 row is hidden but
                                      // its reel rows should still surface on the P2A row.
                                      const reelRows = process.details?.rows || (workOrder.processes?.process1?.rows || []);
                                      if (!reelRows.length) return null;
                                      const basePrice = process.details?.basePrice ?? (workOrder.processes?.process1?.base_price || 0);
                                      const flutePrice = process.details?.flutePrice ?? (workOrder.processes?.process1?.flute_price || 0);
                                      return (
                                    <div className="bg-white border border-slate-200 p-4" data-testid={`reel-breakdown-${process.key}`}>
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <ListBullets className="h-4 w-4" />
                                        Reel-by-Reel Breakdown (P1 Corrugation)
                                      </h4>
                                      <div className="space-y-3">
                                        {reelRows.map((row, idx) => (
                                          <div key={idx} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-600">Base Reel:</span>
                                              <span className="font-mono text-slate-900 font-medium">{row.base_reel_id}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-600">Base Weight (Assigned):</span>
                                              <span className="font-mono text-slate-900 font-medium">{row.base_weight} Kg</span>
                                            </div>
                                            <div className="flex justify-between items-center mt-2">
                                              <span className="text-slate-600">Flute Reel:</span>
                                              <span className="font-mono text-slate-900 font-medium">{row.flute_reel_id}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-600">Flute Weight (Assigned):</span>
                                              <span className="font-mono text-slate-900 font-medium">{row.flute_weight} Kg</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {/* Editable Prices for P1 */}
                                      {enablePriceEditing && process.key === "process1" && (
                                        <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                                          <p className="text-xs text-amber-600 font-medium">Modify Prices:</p>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-700 w-24">Base Price:</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              className="h-7 text-xs w-24"
                                              defaultValue={basePrice}
                                              onChange={(e) => updateCustomPrice("process1", "basePrice", e.target.value)}
                                            />
                                            <span className="text-xs text-slate-400">/Kg</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-700 w-24">Flute Price:</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              className="h-7 text-xs w-24"
                                              defaultValue={flutePrice}
                                              onChange={(e) => updateCustomPrice("process1", "flutePrice", e.target.value)}
                                            />
                                            <span className="text-xs text-slate-400">/Kg</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                      );
                                    })()
                                  )}

                                  {/* P1 Loss Calculation via P2A */}
                                  {viewOptions.formulaCalculation && process.key === "process1" && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <Calculator className="h-4 w-4" />
                                        Loss Calculation (via P2A Sheet Counting)
                                      </h4>
                                      <div className="space-y-2 text-sm font-mono">
                                        {!process.details.canCalculateLoss ? (
                                          <div className="bg-amber-50 p-3 rounded border border-amber-200">
                                            <p className="text-amber-700 text-xs">
                                              <strong>Awaiting P2A Completion</strong>
                                            </p>
                                            <p className="text-amber-600 text-xs mt-1">
                                              Corrugation loss is calculated by comparing expected sheets (from P1 output) vs actual sheets counted in P2A.
                                            </p>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="bg-slate-50 p-2 rounded text-xs">
                                              <p className="text-slate-700">Total GSM = Base GSM + (Flute GSM × {process.details.fluteMultiplier || 150}%)</p>
                                              <p>{process.details.baseGSM} + ({process.details.fluteGSM} × {process.details.fluteMultiplier || 150}%) = <span className="font-bold">{process.details.totalGSM}</span></p>
                                            </div>
                                            <div className="bg-slate-50 p-2 rounded text-xs">
                                              <p className="text-slate-700">Single Sheet Weight</p>
                                              <p>= ({process.details.reelSize} × {process.details.cuttingSize} × {process.details.totalGSM}) / 1550</p>
                                              <p>= <span className="font-bold">{(parseFloat(process.details.singleSheetWeightKg) * 1000).toFixed(3)} gm</span></p>
                                            </div>
                                            <div className="bg-blue-50 p-2 rounded text-xs border border-blue-200">
                                              <p className="text-blue-700">Expected Sheets (from {process.details.totalUsedWeight} Kg):</p>
                                              <p className="text-blue-900 font-bold">{process.details.expectedSheets} sheets</p>
                                            </div>
                                            <div className="bg-green-50 p-2 rounded text-xs border border-green-200">
                                              <p className="text-green-700">Actual Sheets (from P2A):</p>
                                              <p className="text-green-900 font-bold">{process.details.actualSheetsFromP2A} sheets</p>
                                            </div>
                                            <div className="bg-red-50 p-2 rounded text-xs border border-red-200">
                                              <p className="text-red-700">Sheet Loss:</p>
                                              <p className="text-red-900 font-bold">{process.details.sheetLoss} sheets = {process.details.weightLoss} Kg</p>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* P1 Planned vs Actual */}
                                  {viewOptions.plannedVsActual && process.key === "process1" && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <ChartBar className="h-4 w-4" />
                                        Material Usage Summary
                                      </h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b border-slate-100">
                                            <th className="text-left py-1 text-slate-700 font-medium">Item</th>
                                            <th className="text-right py-1 text-slate-700 font-medium">Assigned</th>
                                            <th className="text-right py-1 text-slate-700 font-medium">Used</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr className="border-b border-slate-100">
                                            <td className="py-1 text-slate-800">Base Reel Weight</td>
                                            <td className="py-1 text-right font-mono">{process.details.plannedBaseWeight} Kg</td>
                                            <td className="py-1 text-right font-mono text-emerald-600">{process.details.usedBaseWeight} Kg</td>
                                          </tr>
                                          <tr className="border-b border-slate-100">
                                            <td className="py-1 text-slate-800">Flute Reel Weight</td>
                                            <td className="py-1 text-right font-mono">{process.details.plannedFluteWeight} Kg</td>
                                            <td className="py-1 text-right font-mono text-emerald-600">{process.details.usedFluteWeight} Kg</td>
                                          </tr>
                                          <tr className="bg-slate-50">
                                            <td className="py-1 text-slate-700 font-medium">Total Used</td>
                                            <td className="py-1 text-right font-mono">—</td>
                                            <td className="py-1 text-right font-mono font-bold">{process.details.totalUsedWeight} Kg</td>
                                          </tr>
                                          {process.details.canCalculateLoss && (
                                            <>
                                              <tr className="border-t border-slate-200">
                                                <td className="py-1 text-slate-800">Expected Sheets</td>
                                                <td className="py-1 text-right font-mono" colSpan={2}>{process.details.expectedSheets}</td>
                                              </tr>
                                              <tr>
                                                <td className="py-1 text-slate-800">Actual Sheets (P2A)</td>
                                                <td className="py-1 text-right font-mono text-emerald-600" colSpan={2}>{process.details.actualSheetsFromP2A}</td>
                                              </tr>
                                              <tr className="bg-red-50">
                                                <td className="py-1 text-red-700 font-medium">Sheet Loss</td>
                                                <td className="py-1 text-right font-mono text-red-600 font-bold" colSpan={2}>{process.details.sheetLoss} sheets</td>
                                              </tr>
                                            </>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Formula Calculation for P2A */}
                                  {viewOptions.formulaCalculation && (process.key === "process2a" || process.key === "process2") && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <Calculator className="h-4 w-4" />
                                        Formula Calculation (P2A)
                                      </h4>
                                      <div className="space-y-2 text-sm font-mono">
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-700">Total GSM = Base GSM + (Flute GSM × {process.details.fluteMultiplier || 150}%)</p>
                                          <p>{process.details.baseGSM} + ({process.details.fluteGSM} × {process.details.fluteMultiplier || 150}%) = <span className="font-bold">{process.details.totalGSM}</span></p>
                                        </div>
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-700">Single Sheet Weight = (Reel × Cut × GSM) / 1550</p>
                                          <p>({process.details.reelSize} × {process.details.cuttingSize} × {process.details.totalGSM}) / 1550</p>
                                          <p>= <span className="font-bold">{process.details.singleSheetWeightGrams} gm</span></p>
                                        </div>
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-700">Material Loss Weight = Loss Qty × Sheet Weight</p>
                                          <p>{process.details.sheetLoss} × {process.details.singleSheetWeightKg} Kg</p>
                                          <p>= <span className="font-bold text-red-600">{process.details.materialLossWeight} Kg</span></p>
                                        </div>
                                        {useDetailedCalculation && (
                                          <>
                                            <div className="bg-blue-50 p-2 rounded text-xs border border-blue-200">
                                              <p className="text-blue-700 font-medium">Base Paper Loss:</p>
                                              <p className="text-blue-900">{process.details.baseWeightLoss} Kg × Price = ₹{process.details.baseValueLoss}</p>
                                            </div>
                                            <div className="bg-green-50 p-2 rounded text-xs border border-green-200">
                                              <p className="text-green-700 font-medium">Flute Paper Loss:</p>
                                              <p className="text-green-900">{process.details.fluteWeightLoss} Kg × Price = ₹{process.details.fluteValueLoss}</p>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                      {/* Editable Price for P2A */}
                                      {enablePriceEditing && (
                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                          <p className="text-xs text-amber-600 font-medium mb-2">Modify Price:</p>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-700 w-24">Avg Price:</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              className="h-7 text-xs w-24"
                                              defaultValue={process.details.defaultPrice}
                                              onChange={(e) => updateCustomPrice("process2a", "avgPrice", e.target.value)}
                                            />
                                            <span className="text-xs text-slate-400">/Kg</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Formula Calculation for P2B */}
                                  {viewOptions.formulaCalculation && process.key === "process2b" && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <Calculator className="h-4 w-4" />
                                        Formula Calculation (P2B)
                                      </h4>
                                      <div className="space-y-2 text-sm font-mono">
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-500">Paper GSM</p>
                                          <p className="text-slate-900"><span className="font-bold">{process.details.paperGSM}</span></p>
                                        </div>
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-500">Single Paper Weight = (Reel × Cut × GSM) / 1550</p>
                                          <p className="text-slate-900">({process.details.reelSize} × {process.details.cuttingSize} × {process.details.paperGSM}) / 1550</p>
                                          <p className="text-slate-900">= <span className="font-bold">{process.details.singlePaperWeightGrams} gm</span></p>
                                        </div>
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-500">Material Loss Weight = Loss Qty × Paper Weight</p>
                                          <p className="text-slate-900">{process.details.paperLoss} × {process.details.singlePaperWeightKg} Kg</p>
                                          <p>= <span className="font-bold text-red-600">{process.details.materialLossWeight} Kg</span></p>
                                        </div>
                                      </div>
                                      {enablePriceEditing && (
                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                          <p className="text-xs text-amber-600 font-medium mb-2">Modify Price:</p>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-700 w-24">Avg Price:</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              className="h-7 text-xs w-24"
                                              defaultValue={process.details.defaultPrice}
                                              onChange={(e) => updateCustomPrice("process2b", "avgPrice", e.target.value)}
                                            />
                                            <span className="text-xs text-slate-400">/Kg</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Board Loss Info for P3-P10 */}
                                  {viewOptions.formulaCalculation && process.details?.processType === "board" && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <Calculator className="h-4 w-4" />
                                        Board Loss Calculation
                                      </h4>
                                      <div className="space-y-2 text-sm font-mono">
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-500">Board Lost = Planned - Actual</p>
                                          <p className="text-slate-900">{process.details.plannedBoards} - {process.details.actualBoards} = <span className="font-bold text-red-600">{process.details.boardLoss} pcs</span></p>
                                        </div>
                                        <div className="bg-slate-50 p-2 rounded text-xs">
                                          <p className="text-slate-500">Value of Loss = Board Lost × Price</p>
                                          <p className="text-slate-900">{process.details.boardLoss} × ₹{process.details.effectivePrice} = <span className="font-bold text-red-600">₹{process.valueLoss}</span></p>
                                        </div>
                                      </div>
                                      {enablePriceEditing && (
                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                          <p className="text-xs text-amber-600 font-medium mb-2">Modify Price (from Prod & Material Info):</p>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-700 w-24">Board Price:</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              className="h-7 text-xs w-24"
                                              defaultValue={process.details.defaultPrice}
                                              onChange={(e) => updateCustomPrice(process.key, "boardPrice", e.target.value)}
                                            />
                                            <span className="text-xs text-slate-400">/pc</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Planned vs Actual (for non-P1 processes) */}
                                  {viewOptions.plannedVsActual && process.key !== "process1" && (
                                    <div className="bg-white border border-slate-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-3 flex items-center gap-2">
                                        <ChartBar className="h-4 w-4" />
                                        Planned vs Actual
                                      </h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b border-slate-100">
                                            <th className="text-left py-1 text-slate-900 font-medium">Item</th>
                                            <th className="text-right py-1 text-slate-900 font-medium">Planned</th>
                                            <th className="text-right py-1 text-slate-900 font-medium">Actual</th>
                                            <th className="text-right py-1 text-slate-900 font-medium">Loss</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {/* P2A - Sheets */}
                                          {(process.key === "process2a" || process.key === "process2") && process.details.plannedSheets !== undefined && (
                                            <tr className="border-b border-slate-100">
                                              <td className="py-1 text-slate-900">Sheet Counting</td>
                                              <td className="py-1 text-right font-mono text-slate-900">{process.details.plannedSheets} Nos</td>
                                              <td className="py-1 text-right font-mono text-emerald-600">{process.details.actualSheets} Nos</td>
                                              <td className="py-1 text-right font-mono text-red-600">{process.details.sheetLoss} Nos</td>
                                            </tr>
                                          )}
                                          {/* P2B - Papers */}
                                          {process.key === "process2b" && process.details.plannedPapers !== undefined && (
                                            <tr className="border-b border-slate-100">
                                              <td className="py-1 text-slate-900">Paper Counting</td>
                                              <td className="py-1 text-right font-mono text-slate-900">{process.details.plannedPapers} Nos</td>
                                              <td className="py-1 text-right font-mono text-emerald-600">{process.details.actualPapers} Nos</td>
                                              <td className="py-1 text-right font-mono text-red-600">{process.details.paperLoss} Nos</td>
                                            </tr>
                                          )}
                                          {/* P3-P10 - Boards */}
                                          {process.details?.processType === "board" && (
                                            <tr className="border-b border-slate-100">
                                              <td className="py-1 text-slate-900">Board Counting</td>
                                              <td className="py-1 text-right font-mono text-slate-900">{process.details.plannedBoards} Nos</td>
                                              <td className="py-1 text-right font-mono text-emerald-600">{process.details.actualBoards} Nos</td>
                                              <td className="py-1 text-right font-mono text-red-600">{process.details.boardLoss} Nos</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Process Flow Loss (Chain tracking) for board processes */}
                                  {viewOptions.processFlowLoss && chainLossData[process.key] && (
                                    <div className="bg-white border border-indigo-200 p-4">
                                      <h4 className="text-xs uppercase tracking-wider text-indigo-700 font-semibold mb-3 flex items-center gap-2">
                                        <ListBullets className="h-4 w-4" />
                                        Process Flow Loss (Actual-to-Actual)
                                      </h4>
                                      {chainLossData[process.key].isSource ? (
                                        <div className="space-y-2 text-sm">
                                          <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
                                            <p className="text-indigo-700 text-xs font-medium">Source Process</p>
                                            <p className="text-indigo-900 text-xs mt-1">
                                              Starting point of the production chain. Enter the actual count manually.
                                            </p>
                                          </div>
                                          {/* Planned + Actual side-by-side */}
                                          <div className="grid grid-cols-2 gap-2">
                                            <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                              <p className="text-blue-700 text-xs font-sans">Planned</p>
                                              <p className="text-blue-900 text-base font-mono font-bold mt-1">{chainLossData[process.key].planned > 0 ? `${chainLossData[process.key].planned} Nos` : "—"}</p>
                                            </div>
                                            <div className="bg-green-50 p-2 rounded border border-green-200">
                                              <p className="text-green-700 text-xs font-sans">Actual (manual)</p>
                                              <div className="mt-1 flex items-center gap-2">
                                                <Input
                                                  type="number"
                                                  value={pflEditing[process.key] !== undefined ? pflEditing[process.key] : getPflActualValue(process.key)}
                                                  onChange={(e) => onPflActualChange(process.key, e.target.value)}
                                                  onBlur={() => savePflActual(process.key)}
                                                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                                                  className="h-8 w-28 text-sm font-mono"
                                                  placeholder="Actual"
                                                  disabled={!!pflSaving[process.key]}
                                                  data-testid={`pfl-actual-input-${process.key}`}
                                                />
                                                <span className="text-green-900 text-xs font-mono">Nos</span>
                                                {pflSaving[process.key] && <span className="text-green-600 text-xs font-sans">Saving…</span>}
                                              </div>
                                            </div>
                                          </div>
                                          {/* Planned vs Actual loss + Previous WO benchmark */}
                                          {chainLossData[process.key].planned > 0 && chainLossData[process.key].actualOutput > 0 && (
                                            <div className={`p-2 rounded text-xs border ${chainLossData[process.key].plannedLoss > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                                              <p className={`font-sans ${chainLossData[process.key].plannedLoss > 0 ? "text-red-700" : "text-emerald-700"}`}>Loss vs Planned:</p>
                                              <p className={`font-bold font-mono ${chainLossData[process.key].plannedLoss > 0 ? "text-red-900" : "text-emerald-900"}`}>
                                                {chainLossData[process.key].plannedLoss} Nos ({chainLossData[process.key].plannedLossPercent}%)
                                              </p>
                                              <p className="text-slate-500 mt-1 font-sans">
                                                = Planned ({chainLossData[process.key].planned}) − Actual ({chainLossData[process.key].actualOutput})
                                              </p>
                                              {prevWoLossByProcess?.[process.key] !== undefined && (
                                                <p className="text-slate-600 mt-2 pt-2 border-t border-slate-200 font-sans">
                                                  <span className="text-slate-500">Same item, previous WO ({prevWoLossWoNumber}):</span>{" "}
                                                  <b className="font-mono">{prevWoLossByProcess[process.key]}%</b>
                                                  {chainLossData[process.key].plannedLossPercent != null && (
                                                    <span className={`ml-2 font-mono text-[11px] ${parseFloat(chainLossData[process.key].plannedLossPercent) > prevWoLossByProcess[process.key] ? "text-red-700" : "text-emerald-700"}`}>
                                                      ({parseFloat(chainLossData[process.key].plannedLossPercent) > prevWoLossByProcess[process.key] ? "↑ worse" : "↓ better"})
                                                    </span>
                                                  )}
                                                </p>
                                              )}
                                            </div>
                                          )}
                                          {chainLossData[process.key].effectiveUps > 1 && chainLossData[process.key].actualOutput > 0 && (
                                            <p className="text-indigo-600 text-xs font-sans mt-1">
                                              UPS: {chainLossData[process.key].effectiveUps} → Next process expects: {chainLossData[process.key].actualOutput * chainLossData[process.key].effectiveUps} Nos
                                            </p>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="space-y-2 text-sm font-mono">
                                          <div className="bg-blue-50 p-2 rounded text-xs border border-blue-200">
                                            <p className="text-blue-700">Expected Input (from {chainLossData[process.key].previousProcess}):</p>
                                            <p className="text-blue-900 font-bold">
                                              {chainLossData[process.key].expectedInput} Nos
                                              {chainLossData[process.key].upsApplied && (
                                                <span className="text-blue-600 font-normal ml-2">
                                                  ({chainLossData[process.key].previousActual} × {chainLossData[process.key].upsVal} UPS)
                                                </span>
                                              )}
                                            </p>
                                          </div>
                                          {/* Planned + Actual side-by-side */}
                                          <div className="grid grid-cols-2 gap-2">
                                            <div className="bg-slate-50 p-2 rounded border border-slate-200">
                                              <p className="text-slate-700 text-xs font-sans">Planned (target)</p>
                                              <p className="text-slate-900 text-base font-mono font-bold mt-1">{chainLossData[process.key].planned > 0 ? `${chainLossData[process.key].planned} Nos` : "—"}</p>
                                            </div>
                                            <div className="bg-green-50 p-2 rounded border border-green-200">
                                              <p className="text-green-700 text-xs font-sans">Actual (manual)</p>
                                              <div className="mt-1 flex items-center gap-2">
                                                <Input
                                                  type="number"
                                                  value={pflEditing[process.key] !== undefined ? pflEditing[process.key] : getPflActualValue(process.key)}
                                                  onChange={(e) => onPflActualChange(process.key, e.target.value)}
                                                  onBlur={() => savePflActual(process.key)}
                                                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                                                  className="h-8 w-28 text-sm font-mono"
                                                  placeholder="Actual"
                                                  disabled={!!pflSaving[process.key]}
                                                  data-testid={`pfl-actual-input-${process.key}`}
                                                />
                                                <span className="text-green-900 text-xs font-mono">Nos</span>
                                                {pflSaving[process.key] && <span className="text-green-600 text-xs font-sans">Saving…</span>}
                                              </div>
                                            </div>
                                          </div>
                                          {/* Loss vs Planned + Loss vs Previous (chain) */}
                                          {chainLossData[process.key].actualOutput > 0 && (
                                            <>
                                              {chainLossData[process.key].planned > 0 && (
                                                <div className={`p-2 rounded text-xs border ${chainLossData[process.key].plannedLoss > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                                                  <p className={`font-sans ${chainLossData[process.key].plannedLoss > 0 ? "text-amber-700" : "text-emerald-700"}`}>Loss vs Planned:</p>
                                                  <p className={`font-bold ${chainLossData[process.key].plannedLoss > 0 ? "text-amber-900" : "text-emerald-900"}`}>
                                                    {chainLossData[process.key].plannedLoss} Nos ({chainLossData[process.key].plannedLossPercent}%)
                                                  </p>
                                                  <p className="text-slate-500 mt-1 font-sans">
                                                    = Planned ({chainLossData[process.key].planned}) − Actual ({chainLossData[process.key].actualOutput})
                                                  </p>
                                                </div>
                                              )}
                                              <div className={`p-2 rounded text-xs border ${chainLossData[process.key].chainLoss > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                                                <p className={`font-sans ${chainLossData[process.key].chainLoss > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                                  Process Flow Loss (from previous):
                                                </p>
                                                <p className={`font-bold ${chainLossData[process.key].chainLoss > 0 ? "text-red-900" : "text-emerald-900"}`}>
                                                  {chainLossData[process.key].chainLoss} Nos ({chainLossData[process.key].chainLossPercent}%)
                                                </p>
                                                <p className="text-slate-500 mt-1 font-sans">
                                                  = Expected ({chainLossData[process.key].expectedInput}) − Actual ({chainLossData[process.key].actualOutput})
                                                </p>
                                              </div>
                                            </>
                                          )}
                                          {!chainLossData[process.key].actualOutput && (
                                            <div className="bg-amber-50 p-2 rounded text-xs border border-amber-200">
                                              <p className="text-amber-700 font-sans">Enter Actual Output above to see the losses for this process.</p>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* No options selected message */}
                                  {!viewOptions.reelBreakdown && !viewOptions.formulaCalculation && !viewOptions.plannedVsActual && !viewOptions.processFlowLoss && (
                                    <div className="lg:col-span-3 text-center py-8 text-slate-400">
                                      Select at least one view option above to see details
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Total Summary */}
        {processesWithData.length > 0 && (
          <div className="mt-6 bg-white border border-red-200">
            <div className="px-4 py-3 border-b border-red-200 bg-red-50">
              <div className="flex items-center gap-2">
                <Warning className="h-4 w-4 text-red-600" weight="fill" />
                <span className="font-heading text-sm font-medium text-red-900">Total Loss Summary (All Processes)</span>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Total Value Loss</p>
                {(() => {
                  // Total Value Loss = (Stitching planned − Stitching manual actual) × per-box price
                  const p10Info = workOrder.processes?.process10 || {};
                  const p10Act = workOrder.process_actuals?.process10 || {};
                  const stitchingPlanned =
                    parseInt(p10Act.planned_board_counting) ||
                    parseInt(p10Act.planned_sheet_counting) ||
                    parseInt(p10Info.board_counting) ||
                    parseInt(p10Info.sheet_counting) ||
                    parseInt(p10Info.counting) ||
                    parseInt(p10Info.total_boxes) ||
                    0;
                  const stitchingActual = p10Act.pfl_actual !== undefined && p10Act.pfl_actual !== null && p10Act.pfl_actual !== ""
                    ? parseFloat(p10Act.pfl_actual) || 0
                    : 0;
                  const rate = getItemRate();
                  const lossCount = Math.max(0, stitchingPlanned - stitchingActual);
                  const valueLoss = lossCount * rate;
                  return (
                    <>
                      <p className="font-mono text-2xl text-red-600">₹{valueLoss.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-500 mt-1 normal-case tracking-normal">
                        ({stitchingPlanned} planned − {stitchingActual || "—"} actual) × ₹{rate.toFixed(2)} / box at Stitching
                      </p>
                    </>
                  );
                })()}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Avg Loss %</p>
                <p className="font-mono text-2xl text-red-600">
                  {(() => {
                    const validPercentages = processesWithData.filter((p) => p.percentageLoss !== "—" && parseFloat(p.percentageLoss) > 0);
                    const avg = validPercentages.length > 0
                      ? validPercentages.reduce((sum, p) => sum + parseFloat(p.percentageLoss), 0) / validPercentages.length
                      : 0;
                    return avg.toFixed(2);
                  })()}
                  %
                </p>
              </div>
              <div className="border-l border-red-200 pl-4">
                <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Work Order Status</p>
                <span
                  className={`inline-block px-2 py-1 text-xs uppercase tracking-wider font-medium ${
                    workOrder.status === "completed"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                >
                  {workOrder.status?.replace("_", " ") || "available"}
                </span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductionTrackingDetail;
